import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Modal } from '@/components/ui';
import type { KeycloakAccountRow } from './types';

interface Props {
    row: KeycloakAccountRow | null;
    pending: boolean;
    error: string | null;
    onClose: () => void;
    onConfirm: (sub: string) => void;
}

/**
 * EAA-1820 (frame 73): single-account delete — username in the title, the two
 * consequences the audit/assignments systems impose, an optional assignments
 * acknowledgment, and typing the username (not the sub) before the destructive
 * button enables. Replaces the old sub-field + nested ConfirmButton shape.
 */
export function DeleteAccountDialog({ row, pending, error, onClose, onConfirm }: Props) {
    const { t } = useTranslation();
    const [typed, setTyped] = useState('');
    const [assignmentsAck, setAssignmentsAck] = useState(false);

    const open = Boolean(row);
    const username = row?.username ?? '';
    const confirmed = typed.trim() === username;
    const placementCount = row?.placement?.count ?? 0;
    const needsAssignmentAck = placementCount > 0;

    const close = () => {
        setTyped('');
        setAssignmentsAck(false);
        onClose();
    };

    const canSubmit = confirmed && (!needsAssignmentAck || assignmentsAck);

    return (
        <Modal
            open={open}
            onCancel={close}
            title={t('console.accounts.deleteModal.titleForUser', { username })}
            footer={
                <>
                    <span className="acc-delete-footer-note">{t('console.accounts.deleteModal.auditFooter')}</span>
                    <button type="button" className="portal-btn" onClick={close} disabled={pending}>
                        {t('common.cancel')}
                    </button>
                    <button
                        type="button"
                        className="portal-btn portal-btn-danger"
                        disabled={!canSubmit || pending || !row}
                        onClick={() => row && onConfirm(row.sub)}
                    >
                        {pending ? t('console.accounts.deleteModal.pending') : t('console.accounts.deleteModal.submit')}
                    </button>
                </>
            }
        >
            <div className="portal-admin-form acc-delete-dialog">
                <p className="portal-admin-hint">{t('console.accounts.deleteModal.hintImmediate')}</p>

                <div className="acc-delete-consequences-box" role="note">
                    <strong>{t('console.accounts.deleteModal.notDeletedHeading')}</strong>
                    <ul className="acc-delete-consequences">
                        <li>
                            <strong>{t('console.accounts.bulkDeleteModal.auditLogTerm')}</strong>{' '}
                            {t('console.accounts.bulkDeleteModal.auditLogNote')}
                        </li>
                        <li>
                            <strong>{t('console.accounts.bulkDeleteModal.assignmentsTerm')}</strong>{' '}
                            {t('console.accounts.bulkDeleteModal.assignmentsNote')}
                        </li>
                    </ul>
                </div>

                {needsAssignmentAck && (
                    <label className="acc-delete-assignments-ack">
                        <input
                            type="checkbox"
                            checked={assignmentsAck}
                            onChange={(e) => setAssignmentsAck(e.target.checked)}
                        />
                        {t('console.accounts.deleteModal.assignmentsAck', { count: placementCount })}
                    </label>
                )}

                <label>
                    <Trans
                        i18nKey="console.accounts.deleteModal.confirmLabel"
                        values={{ username }}
                        components={{ code: <code /> }}
                    />
                    <input
                        value={typed}
                        onChange={(e) => setTyped(e.target.value)}
                        placeholder={username}
                        autoComplete="off"
                        autoFocus
                    />
                </label>

                {error && <p className="portal-admin-error">{error}</p>}
            </div>
        </Modal>
    );
}

export default DeleteAccountDialog;
